import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Box, Text, VStack, HStack, Badge, Flex, Progress,
} from '@chakra-ui/react';
import { FiCheckCircle, FiXCircle, FiClock, FiLoader } from 'react-icons/fi';

const TESTS = [
  { n: 1, label: 'SMTP Connect' },
  { n: 2, label: 'Send Email' },
  { n: 3, label: 'IMAP Connect' },
  { n: 4, label: 'Open Inbox' },
  { n: 5, label: 'Find Test Email' },
];

function TestRow({ test, result }) {
  const isPending = !result;
  const isRunning = result?.running;
  const pass = result?.pass;

  return (
    <Flex align="center" gap={3} py={1.5} borderBottom="1px solid" borderColor="gray.100">
      <Box w={5} flexShrink={0} textAlign="center">
        {isPending
          ? <Box w={3} h={3} borderRadius="full" bg="gray.200" mx="auto" />
          : isRunning
          ? <Box as={FiLoader} size={14} color="blue.400" className="spin" />
          : pass
          ? <Box as={FiCheckCircle} size={16} color="green.500" />
          : <Box as={FiXCircle} size={16} color="red.500" />}
      </Box>
      <Text fontSize="sm" fontWeight="600" flex={1} color={isPending ? 'gray.400' : 'gray.700'}>
        Test {test.n}: {test.label}
      </Text>
      {!isPending && !isRunning && (
        <Badge
          fontSize="10px"
          colorScheme={pass ? 'green' : 'red'}
          variant="solid"
          px={2}
        >
          {pass ? 'PASS' : 'FAIL'}
        </Badge>
      )}
      {isRunning && <Badge fontSize="10px" colorScheme="blue" variant="subtle">Running…</Badge>}
    </Flex>
  );
}

export default function EmailTestModal({ isOpen, onClose, account }) {
  const [logs, setLogs]         = useState([]);
  const [results, setResults]   = useState({});
  const [running, setRunning]   = useState(false);
  const [finished, setFinished] = useState(false);
  const [countdown, setCountdown] = useState(null); // { seconds, attempt, maxRetries }
  const logRef  = useRef(null);
  const evsRef  = useRef(null);

  const startTest = useCallback(() => {
    if (!account) return;
    setLogs([]);
    setResults({});
    setRunning(true);
    setFinished(false);
    setCountdown(null);

    // Close any existing stream
    if (evsRef.current) { evsRef.current.close(); evsRef.current = null; }

    // Use fetch with ReadableStream for SSE (avoids EventSource auth header limitation)
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const ctrl = new AbortController();
    evsRef.current = ctrl;

    fetch(`/api/email/accounts/${account.id}/test`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      signal: ctrl.signal,
    }).then(async (resp) => {
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop(); // keep incomplete chunk
        for (const part of parts) {
          const line = part.replace(/^data: /, '').trim();
          if (!line) continue;
          try {
            const msg = JSON.parse(line);
            if (msg.type === 'log') {
              setLogs(l => [...l, { text: msg.msg, level: msg.level || 'info' }]);
            } else if (msg.type === 'test') {
              setResults(r => ({ ...r, [msg.n]: { pass: msg.pass, running: false } }));
            } else if (msg.type === 'countdown') {
              setCountdown({ seconds: msg.seconds, attempt: msg.attempt, maxRetries: msg.maxRetries });
            } else if (msg.type === 'done') {
              setRunning(false);
              setFinished(true);
              setCountdown(null);
            }
          } catch(e) {}
        }
      }
      setRunning(false);
      setFinished(true);
      setCountdown(null);
    }).catch((e) => {
      if (e.name !== 'AbortError') {
        setLogs(l => [...l, { text: `Connection error: ${e.message}`, level: 'error' }]);
        setRunning(false);
        setFinished(true);
      }
    });
  }, [account]);

  // Auto-start when modal opens
  useEffect(() => {
    if (isOpen && account) {
      startTest();
    }
    return () => {
      if (evsRef.current) { evsRef.current.abort?.(); evsRef.current = null; }
    };
  }, [isOpen, account]); // eslint-disable-line

  // Auto-scroll logs
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logs]);

  const handleClose = () => {
    if (evsRef.current) { evsRef.current.abort?.(); evsRef.current = null; }
    setRunning(false);
    onClose();
  };

  const allPass = finished && TESTS.every(t => results[t.n]?.pass);
  const anyFail = finished && TESTS.some(t => results[t.n] !== undefined && !results[t.n]?.pass);

  return (
    <>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; display: inline-block; }`}</style>
      <Modal isOpen={isOpen} onClose={handleClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="85vh">
          <ModalHeader fontSize="md" pb={2}>
            <HStack spacing={2} align="center">
              <Text>Email Account Test</Text>
              {account && (
                <Badge fontSize="10px" colorScheme="blue" variant="outline">{account.name || account.email_address}</Badge>
              )}
              {running && <Badge fontSize="10px" colorScheme="orange" variant="subtle">Running…</Badge>}
              {finished && allPass && <Badge fontSize="10px" colorScheme="green" variant="solid">All Tests Passed</Badge>}
              {finished && anyFail && <Badge fontSize="10px" colorScheme="red" variant="solid">Tests Failed</Badge>}
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">

              {/* Test status rows */}
              <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
                <Box bg="gray.50" px={3} py={1.5} borderBottom="1px solid" borderColor="gray.200">
                  <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.05em">
                    Test Results
                  </Text>
                </Box>
                <Box px={3} py={1}>
                  {TESTS.map(t => (
                    <TestRow key={t.n} test={t} result={results[t.n]} />
                  ))}
                </Box>
              </Box>

              {/* Countdown / retry indicator */}
              {countdown && (
                <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3}>
                  <HStack spacing={2} mb={1}>
                    <Box as={FiClock} size={14} color="orange.500" />
                    <Text fontSize="xs" fontWeight="600" color="orange.700">
                      Retry {countdown.attempt}/{countdown.maxRetries} — waiting {countdown.seconds}s for email delivery…
                    </Text>
                  </HStack>
                  <Progress
                    value={(10 - countdown.seconds) * 10}
                    size="xs"
                    colorScheme="orange"
                    borderRadius="full"
                  />
                </Box>
              )}

              {/* Console log */}
              <Box>
                <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase"
                  letterSpacing="0.05em" mb={1}>Console Output</Text>
                <Box
                  ref={logRef}
                  bg="gray.900"
                  borderRadius="md"
                  p={3}
                  h="200px"
                  overflowY="auto"
                  fontFamily="'Courier New', monospace"
                  fontSize="11px"
                >
                  {logs.length === 0 && (
                    <Text color="gray.500">Waiting for output…</Text>
                  )}
                  {logs.map((entry, i) => (
                    <Text key={i} color={
                      entry.level === 'error' ? 'red.300'
                      : entry.level === 'success' ? 'green.300'
                      : entry.level === 'warn' ? 'yellow.300'
                      : 'gray.300'
                    } whiteSpace="pre-wrap" lineHeight="1.6">
                      {entry.text}
                    </Text>
                  ))}
                  {running && (
                    <Text color="blue.300" mt={1}>█</Text>
                  )}
                </Box>
              </Box>

            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            {(finished || !running) && (
              <Button size="sm" leftIcon={<FiLoader />} colorScheme="blue" variant="outline"
                onClick={startTest} isLoading={running}>
                Run Again
              </Button>
            )}
            <Button size="sm" onClick={handleClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
